// ============================================================================
// Node Config Validation
// Returns array of error messages. Empty array = valid.
// ============================================================================
import i18n from '../../i18n';

export interface ValidationResult {
  nodeId: string;
  nodeLabel: string;
  errors: string[];
}

export interface ValidationContext {
  /** Number of accounts the user has. When 0 (new user), skip account selection requirement. */
  availableAccountCount?: number;
}

function getConfig(data: any, nodeType: string): Record<string, any> {
  switch (nodeType) {
    case 'account_selector': return data.account_selector_config || data.account_selector || data.config || {};
    case 'brand_info': return data.brand_info_config || data.config || {};
    case 'seeding_contents_manager': return data.seeding_contents_manager_config || data.config || {};
    case 'post_to_profile': return data.post_to_profile_config || data.postToProfileConfig || data.config || {};
    case 'post_to_group': return data.post_to_group_config || data.postToGroupConfig || data.config || {};
    case 'post_to_fanpage': return data.post_to_fanpage_config || data.config || {};
    case 'fanpage_comment_on_post': return data.fanpage_comment_on_post_config || data.config || {};
    case 'fanpage_reply_to_comments': return data.fanpage_reply_to_comments_config || data.config || {};
    case 'fanpage_like_comments': return data.fanpage_like_comments_config || data.config || {};
    case 'fanpage_moderate_comments': return data.fanpage_moderate_comments_config || data.config || {};
    case 'fanpage_private_reply': return data.fanpage_private_reply_config || data.config || {};
    case 'facebook_read_insight': return data.facebook_read_insight_config || data.config || {};
    case 'comment_on_post': return data.comment_on_post_config || data.commentOnPostConfig || data.config || {};
    case 'reply_to_comments': return data.reply_to_comments_config || data.config || {};
    case 'filter_post': return data.filter_post_config || data.config || {};
    case 'find_trend_fb': return data.find_trend_fb_config || data.config || {};
    case 'share_url': return data.share_url_config || data.config || {};
    case 'search_groups': return data.search_groups_config || data.config || {};
    case 'join_groups': return data.join_groups_config || data.config || {};
    case 'like_pages': return data.like_pages_config || data.config || {};
    case 'like_posts': return data.like_posts_config || data.config || {};
    case 'like_post_tiktok': return data.like_post_tiktok_config || data.config || {};
    case 'share_post_tiktok': return data.share_post_tiktok_config || data.config || {};
    case 'like_post_instagram': return data.like_post_instagram_config || data.config || {};
    case 'create_post_instagram': return data.create_post_instagram_config || data.config || {};
    case 'repost_post_instagram': return data.repost_post_instagram_config || data.config || {};
    case 'comment_post_instagram': return data.comment_post_instagram_config || data.config || {};
    case 'follow_instagram': return data.follow_instagram_config || data.config || {};
    case 'reply_comment_instagram': return data.reply_comment_instagram_config || data.config || {};
    case 'comment_post_tiktok': return data.comment_post_tiktok_config || data.config || {};
    case 'reply_comment_tiktok': return data.reply_comment_tiktok_config || data.config || {};
    case 'like_comment_tiktok': return data.like_comment_tiktok_config || data.config || {};
    case 'interact': return data.interact_config || data.config || {};
    case 'find_join_groups': return data.find_join_groups_config || data.find_groups_config || data.find_groups || data.config || {};
    case 'find_group_posts': return data.find_group_posts_config || data.config || {};
    case 'ai_text_process': return data.ai_text_process_config || data.config || {};
    case 'ai_generate_asset': return data.ai_generate_asset_config || data.config || {};
    case 'view_notifications': return data.view_notifications_config || data.config || {};
    case 'accept_friend_requests': return data.accept_friend_requests_config || data.config || {};
    case 'search_add_friend': return data.search_add_friend_config || data.config || {};
    case 'find_top_posts': return data.find_top_posts_config || data.config || {};
    case 'send_direct_message': return data.send_direct_message_config || data.config || {};
    case 'post_auto_responder': return data.post_auto_responder_config || data.config || {};
    case 'group_random_messenger': return data.group_random_messenger_config || data.config || {};
    case 'check_message': return data.check_message_config || data.config || {};
    case 'ai_remake_image': return data.ai_remake_image_config || data.ai_grok_remake_image_config || data.config || {};
    case 'ai_grok_remake_image': return data.ai_grok_remake_image_config || data.ai_remake_image_config || data.config || {};
    case 'zalo_add_friend': return data.zalo_add_friend_config || data.config || {};
    case 'zalo_join_group': return data.zalo_join_group_config || data.config || {};
    case 'zalo_group_members': return data.zalo_group_members_config || data.config || {};
    case 'zalo_proactive_sender': return data.zalo_proactive_sender_config || data.config || {};
    case 'find_trend_omnichannel': return data.find_trend_omnichannel_config || data.config || {};
    case 'download_video': return data.download_video_config || data.config || {};
    case 'analyze_viral_content': return data.analyze_viral_content_config || data.config || {};
    case 'auto_repurpose_video': return data.auto_repurpose_video_config || data.config || {};
    case 'vault_upload_file': return data.vault_upload_file_config || data.config || {};
    default: return data.config || {};
  }
}

function validateConfig(nodeType: string, cfg: Record<string, any>, context?: ValidationContext): string[] {
  const errors: string[] = [];
  const t = (key: string, opts?: any): string => i18n.t(key, { ns: 'workflow', ...opts }) as string;

  switch (nodeType) {
    case 'account_selector': {
      if (!cfg.platform) errors.push(t('val_platformRequired'));
      const accs = cfg.selected_accounts || [];
      const selectedFanpages = cfg.selected_fanpages || [];
      const fanpageOnlySelection = cfg.platform === 'facebook' && selectedFanpages.length > 0;
      // Only require account selection when user actually has accounts available
      if (accs.length === 0 && !fanpageOnlySelection && (context?.availableAccountCount ?? 1) > 0) {
        errors.push(t('val_accountRequired'));
      }
      break;
    }
    case 'brand_info': {
      if (!cfg.brand_name?.trim()) errors.push(t('val_brandNameRequired'));
      if (!cfg.industry?.trim()) errors.push(t('val_industryRequired'));
      if (!cfg.description?.trim()) errors.push(t('val_descriptionRequired'));
      break;
    }
    case 'seeding_contents_manager': {
      if (!cfg.name?.trim()) errors.push(t('val_managerNameRequired'));
      const sa = cfg.seeding_accounts || [];
      if (sa.length === 0) errors.push(t('val_seedingAccountRequired'));
      break;
    }
    case 'post_to_profile': {
      if (!cfg.content_type) errors.push(t('val_contentTypeRequired'));
      break;
    }
    case 'post_to_group': {
      const groups = cfg.group_urls || [];
      if (groups.length === 0) errors.push(t('val_groupUrlRequired'));
      break;
    }
    case 'post_to_fanpage': {
      if (!cfg.content_type) errors.push(t('val_contentTypeRequired'));
      if ((cfg.content_source_mode || 'ai') === 'manual' && !cfg.manual_message?.trim()) {
        errors.push(t('val_contentRequired', 'Manual content is required'));
      }
      break;
    }
    case 'fanpage_comment_on_post': {
      if (!cfg.content_hints?.trim()) errors.push(t('val_contentGuidanceRequired'));
      if (!cfg.selected_post_id?.trim()) {
        errors.push(t('val_postRequired', 'Select at least one fanpage post'));
      }
      break;
    }
    case 'fanpage_reply_to_comments': {
      if (!cfg.content_hints?.trim()) errors.push(t('val_contentGuidanceRequired'));
      if (!cfg.selected_post_id?.trim()) {
        errors.push(t('val_postRequired', 'Select at least one fanpage post'));
      }
      break;
    }
    case 'fanpage_like_comments': {
      if (!cfg.selected_post_id?.trim()) {
        errors.push(t('val_postRequired', 'Select at least one fanpage post'));
      }
      break;
    }
    case 'fanpage_moderate_comments': {
      if (!cfg.moderation_action) errors.push(t('val_actionRequired', 'Choose a moderation action'));
      if (!cfg.selected_post_id?.trim()) {
        errors.push(t('val_postRequired', 'Select at least one fanpage post'));
      }
      break;
    }
    case 'fanpage_private_reply': {
      if ((cfg.message_source_mode || 'ai') === 'manual' && !cfg.manual_message?.trim()) {
        errors.push(t('val_messageRequired', 'Manual message is required'));
      }
      if (!cfg.selected_post_id?.trim()) {
        errors.push(t('val_postRequired', 'Select at least one fanpage post'));
      }
      break;
    }
    case 'comment_on_post': {
      const dispatch = cfg.dispatch_mode || 'specific';
      if (dispatch === 'specific' && !cfg.post_url?.trim()) {
        errors.push(t('val_postUrlRequired'));
      }
      if (dispatch === 'managed' && !cfg.seeding_contents_manager_node_id) {
        errors.push(t('val_scmNodeRequired'));
      }
      if (dispatch === 'group_posts' && !cfg.find_group_posts_node_id) {
        errors.push(t('val_fgpNodeRequired'));
      }
      if (dispatch === 'filter_posts' && !cfg.filter_post_node_id) {
        errors.push(t('val_fpNodeRequired'));
      }
      if (dispatch === 'from_top_posts' && !cfg.find_top_posts_node_id) {
        errors.push(t('val_ftpNodeRequired'));
      }
      break;
    }
    case 'reply_to_comments': {
      const dispatch = cfg.dispatch_mode || 'specific';
      if (dispatch === 'specific' && !cfg.post_url?.trim()) {
        errors.push(t('val_postUrlRequired'));
      }
      if (dispatch === 'managed' && !cfg.seeding_contents_manager_node_id) {
        errors.push(t('val_scmNodeRequired'));
      }
      if (dispatch === 'group_posts' && !cfg.find_group_posts_node_id) {
        errors.push(t('val_fgpNodeRequired'));
      }
      if (dispatch === 'filter_posts' && !cfg.filter_post_node_id) {
        errors.push(t('val_fpNodeRequired'));
      }
      if (dispatch === 'from_top_posts' && !cfg.find_top_posts_node_id) {
        errors.push(t('val_ftpNodeRequired'));
      }
      break;
    }
    case 'filter_post': {
      if (!cfg.keyword?.trim()) {
        errors.push(t('val_keywordRequired'));
      }
      break;
    }
    case 'find_trend_fb': {
      break;
    }
    case 'share_url': {
      const shareMode = cfg.dispatch_mode || 'specific';
      if (shareMode === 'specific') {
        if (!cfg.url?.trim()) errors.push(t('val_shareUrlRequired'));
      } else if (shareMode === 'managed') {
        if (!cfg.seeding_contents_manager_node_id) errors.push(t('val_scmNodeRequired'));
      } else if (shareMode === 'group_posts') {
        if (!cfg.find_group_posts_node_id) errors.push(t('val_fgpNodeRequired'));
      } else if (shareMode === 'filter_posts') {
        if (!cfg.filter_post_node_id) errors.push(t('val_fpNodeRequired'));
      } else if (shareMode === 'from_top_posts') {
        if (!cfg.find_top_posts_node_id) errors.push(t('val_ftpNodeRequired'));
      }
      break;
    }
    case 'interact': {
      if (!cfg.type) errors.push(t('val_interactionTypeRequired'));
      break;
    }
    case 'find_join_groups': {
      const kws = cfg.keywords || [];
      if (kws.length === 0) errors.push(t('val_findJoinKeywordRequired'));
      break;
    }
    case 'find_group_posts': {
      const groups = cfg.group_urls || [];
      if (groups.length === 0) errors.push(t('val_findGroupUrlRequired'));
      const fgpSearchMode = cfg.search_mode || 'smart_ai';
      if (fgpSearchMode === 'keyword' && !cfg.criteria?.trim()) {
        errors.push(t('val_fgpKeywordCriteriaRequired'));
      }
      for (const url of groups) {
        if (url && !url.includes('facebook.com/groups/')) {
          errors.push(t('val_invalidGroupUrl', { url }));
          break;
        }
      }
      const daysBack = cfg.days_back;
      if (daysBack != null && (daysBack < 0 || !Number.isInteger(daysBack))) {
        errors.push(t('val_daysBackInvalid'));
      }
      break;
    }
    case 'like_pages': {
      const pages = cfg.page_urls || [];
      if (pages.length === 0 && !cfg.from_suggestions) {
        errors.push(t('val_pageUrlsRequired'));
      }
      break;
    }
    case 'like_posts': {
      const likeMode = cfg.dispatch_mode || 'specific';
      if (likeMode === 'specific') {
        const urls = cfg.urls || [];
        if (urls.length === 0) errors.push(t('val_postUrlsRequired'));
      } else if (likeMode === 'managed') {
        if (!cfg.seeding_contents_manager_node_id) errors.push(t('val_scmNodeRequired'));
      } else if (likeMode === 'group_posts') {
        if (!cfg.find_group_posts_node_id) errors.push(t('val_fgpNodeRequired'));
      } else if (likeMode === 'filter_posts') {
        if (!cfg.filter_post_node_id) errors.push(t('val_fpNodeRequired'));
      } else if (likeMode === 'from_top_posts') {
        if (!cfg.find_top_posts_node_id) errors.push(t('val_ftpNodeRequired'));
      }
      break;
    }
    case 'create_post_instagram': {
      if (!cfg.content_type) errors.push(t('val_contentTypeRequired', 'Content type is required'));
      break;
    }
    case 'like_post_instagram':
    case 'repost_post_instagram':
    case 'comment_post_instagram':
    case 'follow_instagram': {
      const urls = cfg.urls || [];
      if (urls.length === 0) errors.push(t('val_postUrlsRequired', 'Post URLs are required'));
      break;
    }
    case 'reply_comment_instagram': {
      const urls = cfg.urls || [];
      if (urls.length === 0) errors.push(t('val_postUrlsRequired', 'Post URLs are required'));
      const keywords = cfg.keywords || [];
      if (keywords.length === 0) errors.push(t('val_keywordsRequired', 'Keywords are required'));
      if (cfg.max_comments !== undefined && (cfg.max_comments < 1 || cfg.max_comments > 300)) {
        errors.push(t('val_maxCommentsInvalid', 'Max comments must be between 1 and 300'));
      }
      break;
    }
    case 'like_post_tiktok': {
      const mode = cfg.dispatch_mode || 'urls';
      if (mode === 'urls') {
        const urls = cfg.urls || [];
        if (urls.length === 0) errors.push(t('val_tiktokUrlsRequired'));
      } else if (mode === 'seeding_for_account') {
        const ids = cfg.tiktok_ids || [];
        if (ids.length === 0) errors.push(t('val_tiktokIdsRequired'));
        if (!cfg.count || cfg.count < 1) errors.push(t('val_tiktokCountRequired'));
      } else if (mode === 'keyword_search') {
        if (!cfg.keyword?.trim()) errors.push(t('val_tiktokKeywordRequired'));
        if (!cfg.count || cfg.count < 1) errors.push(t('val_tiktokCountRequired'));
      }
      break;
    }
    case 'comment_post_tiktok': {
      const mode = cfg.dispatch_mode || 'urls';
      if (mode === 'urls') {
        const urls = cfg.urls || [];
        if (urls.length === 0) errors.push(t('val_tiktokCommentUrlsRequired'));
      } else if (mode === 'seeding_for_account') {
        const ids = cfg.tiktok_ids || [];
        if (ids.length === 0) errors.push(t('val_tiktokIdsRequired'));
        if (!cfg.count || cfg.count < 1) errors.push(t('val_tiktokCommentCountRequired'));
      } else if (mode === 'keyword_search') {
        if (!cfg.keyword?.trim()) errors.push(t('val_tiktokKeywordRequired'));
        if (!cfg.count || cfg.count < 1) errors.push(t('val_tiktokCommentCountRequired'));
      }
      if (cfg.comment_mode === 'ai') {
        if (!cfg.prompt?.trim()) errors.push(t('val_tiktokCommentPromptRequired'));
      } else {
        const fixed = cfg.fixed_comments || [];
        if (fixed.length === 0) errors.push(t('val_tiktokFixedCommentsRequired'));
      }
      break;
    }
    case 'reply_comment_tiktok': {
      const mode = cfg.dispatch_mode || 'urls';
      if (mode === 'urls') {
        const urls = cfg.urls || [];
        if (urls.length === 0) errors.push(t('val_tiktokCommentUrlsRequired'));
      } else if (mode === 'seeding_for_account') {
        const ids = cfg.tiktok_ids || [];
        if (ids.length === 0) errors.push(t('val_tiktokIdsRequired'));
        if (!cfg.count || cfg.count < 1) errors.push(t('val_tiktokCommentCountRequired'));
      } else if (mode === 'keyword_search') {
        if (!cfg.keyword?.trim()) errors.push(t('val_tiktokKeywordRequired'));
        if (!cfg.count || cfg.count < 1) errors.push(t('val_tiktokCommentCountRequired'));
      }
      if ((cfg.reply_mode || 'keyword') === 'keyword') {
        const kws = cfg.keywords || [];
        if (kws.length === 0) errors.push(t('val_tiktokReplyKeywordsRequired'));
      }
      if (!cfg.max_replies || cfg.max_replies < 1) errors.push(t('val_tiktokMaxRepliesRequired'));
      if (cfg.comment_mode === 'ai') {
        if (!cfg.prompt?.trim()) errors.push(t('val_tiktokCommentPromptRequired'));
      } else {
        const fixed = cfg.fixed_comments || [];
        if (fixed.length === 0) errors.push(t('val_tiktokFixedCommentsRequired'));
      }
      break;
    }
    case 'like_comment_tiktok': {
      const mode = cfg.dispatch_mode || 'urls';
      if (mode === 'urls') {
        const urls = cfg.urls || [];
        if (urls.length === 0) errors.push(t('val_tiktokCommentUrlsRequired'));
      } else if (mode === 'seeding_for_account') {
        const ids = cfg.tiktok_ids || [];
        if (ids.length === 0) errors.push(t('val_tiktokIdsRequired'));
        if (!cfg.count || cfg.count < 1) errors.push(t('val_tiktokCommentCountRequired'));
      } else if (mode === 'keyword_search') {
        if (!cfg.keyword?.trim()) errors.push(t('val_tiktokKeywordRequired'));
        if (!cfg.count || cfg.count < 1) errors.push(t('val_tiktokCommentCountRequired'));
      }
      if ((cfg.target_mode || 'keyword') === 'keyword') {
        const kws = cfg.keywords || [];
        if (kws.length === 0) errors.push(t('val_tiktokLikeKeywordsRequired'));
      }
      if (!cfg.max_likes || cfg.max_likes < 1) errors.push(t('val_tiktokMaxLikesRequired'));
      break;
    }
    case 'share_post_tiktok': {
      const mode = cfg.dispatch_mode || 'urls';
      if (mode === 'urls') {
        const urls = cfg.urls || [];
        if (urls.length === 0) errors.push(t('val_tiktokShareUrlsRequired'));
      } else if (mode === 'seeding_for_account') {
        const ids = cfg.tiktok_ids || [];
        if (ids.length === 0) errors.push(t('val_tiktokIdsRequired'));
        if (!cfg.count || cfg.count < 1) errors.push(t('val_tiktokShareCountRequired'));
      } else if (mode === 'keyword_search') {
        if (!cfg.keyword?.trim()) errors.push(t('val_tiktokKeywordRequired'));
        if (!cfg.count || cfg.count < 1) errors.push(t('val_tiktokShareCountRequired'));
      }
      break;
    }
    case 'search_add_friend': {
      const addFriendType = cfg.type || 'keyword';
      if (addFriendType === 'from_top_posts') {
        if (!cfg.find_top_posts_node_id) errors.push(t('val_ftpNodeRequired'));
      } else {
        const kws = cfg.keywords || [];
        if (kws.length === 0) errors.push(t('val_searchKeywordRequired'));
      }
      break;
    }
    case 'ai_generate_asset': {
      if (!cfg.task_type?.trim()) errors.push(t('val_taskTypeRequired'));
      break;
    }
    case 'ai_text_process': {
      if (!cfg.task_type?.trim()) errors.push(t('val_taskTypeRequired'));
      break;
    }
    case 'find_top_posts': {
      const sourceUrls = Array.isArray(cfg.source_urls)
        ? cfg.source_urls.filter((u: any) => typeof u === 'string' && u.trim())
        : [];
      if (sourceUrls.length === 0 && !cfg.source_url?.trim()) errors.push(t('val_sourceUrlRequired'));
      break;
    }
    case 'join_groups': {
      const joinMode = cfg.join_mode || 'random';
      if (joinMode === 'random') {
        const kws = cfg.keywords || [];
        if (kws.length === 0) errors.push(t('val_joinGroupKeywordRequired'));
      } else if (joinMode === 'by_url') {
        const urls = cfg.group_urls || [];
        if (urls.length === 0) errors.push(t('val_groupUrlRequired'));
      }
      break;
    }
    case 'send_direct_message': {
      const recipients = cfg.recipients || [];
      if (recipients.length === 0) errors.push(t('val_recipientsRequired'));
      const messages = cfg.messages || [];
      if (messages.length === 0) errors.push(t('val_messagesRequired'));
      break;
    }
    case 'post_auto_responder': {
      const responderMode = cfg.dispatch_mode || 'specific';
      if (responderMode === 'specific' && !cfg.post_url?.trim()) {
        errors.push(t('val_postUrlRequired'));
      }
      if (responderMode === 'managed' && !cfg.seeding_contents_manager_node_id) {
        errors.push(t('val_scmNodeRequired'));
      }
      if (responderMode === 'group_posts' && !cfg.find_group_posts_node_id) {
        errors.push(t('val_fgpNodeRequired'));
      }
      if (responderMode === 'filter_posts' && !cfg.filter_post_node_id) {
        errors.push(t('val_fpNodeRequired'));
      }
      if (responderMode === 'from_top_posts' && !cfg.find_top_posts_node_id) {
        errors.push(t('val_ftpNodeRequired'));
      }
      const kws = cfg.keywords || [];
      if (kws.length === 0) errors.push(t('val_responderKeywordsRequired'));
      if (!cfg.message?.trim()) errors.push(t('val_responderMessageRequired'));
      break;
    }
    case 'group_random_messenger': {
      if (!cfg.group_url?.trim()) errors.push(t('val_messengerGroupUrlRequired'));
      if (!cfg.message?.trim()) errors.push(t('val_messengerMessageRequired'));
      break;
    }
    case 'zalo_add_friend': {
      if (!cfg.phone_numbers || cfg.phone_numbers.length === 0) {
        errors.push(t('val_phoneNumbersRequired', 'At least one phone number is required.'));
      }
      break;
    }
    case 'zalo_join_group': {
      if (!cfg.invite_link?.trim()) {
        errors.push(t('val_inviteLinkRequired', 'Group invite link is required.'));
      }
      break;
    }
    case 'zalo_group_members': {
      if (!cfg.group_id?.trim() && !cfg.group_name?.trim()) {
        errors.push(t('val_groupIdOrNameRequired', 'Either Group ID or Group Name is required.'));
      }
      break;
    }
    case 'zalo_proactive_sender': {
      if (!cfg.recipients || cfg.recipients.length === 0) {
        errors.push(t('val_recipientsRequired', 'At least one recipient is required.'));
      }
      break;
    }
    case 'find_trend_omnichannel': {
      if (!cfg.platform) errors.push(t('val_platformRequired', 'Nền tảng tìm kiếm là bắt buộc.'));
      if (!cfg.keywords || cfg.keywords.length === 0) errors.push(t('val_keywordsRequired', 'Cần ít nhất một từ khoá tìm kiếm.'));
      break;
    }
    case 'download_video': {
      if (cfg.max_duration && cfg.max_duration <= 0) errors.push(t('val_maxDurationPositive', 'Thời lượng tối đa phải lớn hơn 0.'));
      break;
    }
    case 'analyze_viral_content': {
      break;
    }
    case 'auto_repurpose_video': {
      if (cfg.max_duration_sec && cfg.max_duration_sec <= 0) errors.push(t('val_maxDurationPositive', 'Thời lượng video tối đa phải lớn hơn 0.'));
      break;
    }
    case 'vault_upload_file': {
      if (!cfg.goal?.trim()) errors.push(t('val_goalRequired', 'Mục tiêu lưu trữ Vault là bắt buộc.'));
      break;
    }
  }

  return errors;
}

/** Validate a single node. Returns errors array (empty = valid). */
export function validateSingleNode(node: { id: string; type?: string; data: any }, context?: ValidationContext): string[] {
  const nodeType = node.type || node.data?.type || '';
  const cfg = getConfig(node.data, nodeType);
  return validateConfig(nodeType, cfg, context);
}

/** Validate all nodes. Returns array of results for invalid nodes only. */
export function validateAllNodes(nodes: { id: string; type?: string; data: any }[], context?: ValidationContext): ValidationResult[] {
  const results: ValidationResult[] = [];
  for (const node of nodes) {
    if (node.id === 'data-zone') continue;
    const errors = validateSingleNode(node, context);
    if (errors.length > 0) {
      results.push({
        nodeId: node.id,
        nodeLabel: node.data?.label || node.type || 'Unknown',
        errors,
      });
    }
  }
  return results;
}

